import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type {
  ContractGenerationTemplateVariantInDb,
  ContractGenerationTemplateVariantSummary,
  ContractGenerationTemplateInDb,
  ExtractionResult,
  FieldCatalog,
  TemplateValidationError,
} from '@/api';
import { TemplatesService } from '@/services/contractGeneration/templatesService';
import { TemplateVariantsService } from '@/services/contractGeneration/templateVariantsService';
import { TemplateAuthoringService } from '@/services/contractGeneration/templateAuthoringService';
import {
  blobToBase64,
  exportCurrentDocumentAsDocx,
  replaceDocumentFromDocxBase64,
} from '@/office/word/templateWord';
import { logger } from '@/utils/logger';

export type OfficeTemplateValidationStatus = 'idle' | 'running' | 'done' | 'error';

/**
 * Called before an upload when the document fails validation. Receives the
 * blocking errors; resolve `true` to upload anyway, `false` to abort. The
 * save actions take this so the (UI) "upload anyway?" prompt lives in the
 * caller while the validate→confirm→upload sequence stays in the store.
 */
export type ConfirmInvalidUpload = (errors: TemplateValidationError[]) => Promise<boolean>;

/**
 * State for the Word add-in's Templates tab. Lives in a store (not the view)
 * so the selected template and the last validation survive switching between
 * the task pane's bottom tabs.
 */
const TEMPLATE_PAGE_SIZE = 50;

export const useOfficeTemplateStore = defineStore('officeTemplate', () => {
  const templates = ref<ContractGenerationTemplateInDb[]>([]);
  const templatesLoading = ref<boolean>(false);
  // Pagination over the current search text — the selects load further pages
  // on dropdown scroll instead of capping the list at the first page.
  const templatesQueryText = ref<string>('');
  const templatesPage = ref<number>(1);
  const templatesTotalCount = ref<number>(0);
  const templatesLoadingMore = ref<boolean>(false);
  const selectedTemplateUid = ref<string | null>(null);

  const variants = ref<ContractGenerationTemplateVariantSummary[]>([]);
  const variantsLoading = ref<boolean>(false);
  const selectedVariantUid = ref<string | null>(null);

  const openingInDocument = ref<boolean>(false);

  // Field catalog for the Snippets palette — static per backend deploy, so
  // one fetch per session is enough.
  const fieldCatalog = ref<FieldCatalog | null>(null);
  const fieldCatalogLoading = ref<boolean>(false);

  // Validation always runs against the LIVE Word document (exported via
  // Office.js), never the stored vault file — the user is editing in Word.
  const validationStatus = ref<OfficeTemplateValidationStatus>('idle');
  const validationResult = ref<ExtractionResult | null>(null);
  const validationError = ref<string | null>(null);

  // Captured at selection time (not derived from the list) so the selected
  // template's name survives the list being replaced by a new search/page.
  const selectedTemplate = ref<ContractGenerationTemplateInDb | null>(null);

  const hasMoreTemplates = computed<boolean>(
    () => templates.value.length < templatesTotalCount.value
  );

  const selectedVariant = computed<ContractGenerationTemplateVariantSummary | null>(
    () => variants.value.find(variant => variant.uid === selectedVariantUid.value) ?? null
  );

  async function loadFieldCatalog(): Promise<void> {
    if (fieldCatalog.value || fieldCatalogLoading.value) return;
    fieldCatalogLoading.value = true;
    try {
      fieldCatalog.value = await TemplateAuthoringService.getFieldCatalog();
    } finally {
      fieldCatalogLoading.value = false;
    }
  }

  async function searchTemplates(text: string = ''): Promise<void> {
    templatesQueryText.value = text;
    templatesPage.value = 1;
    templatesLoading.value = true;
    try {
      const result = await TemplatesService.search({ text, page: 1, pageSize: TEMPLATE_PAGE_SIZE });
      // A newer search superseded this one while it was in flight — drop it.
      if (templatesQueryText.value !== text) return;
      templates.value = result.results;
      templatesTotalCount.value = result.result_count;
    } finally {
      if (templatesQueryText.value === text) {
        templatesLoading.value = false;
      }
    }
  }

  /** Append the next page of the current search. No-op when all loaded. */
  async function loadMoreTemplates(): Promise<void> {
    if (templatesLoading.value || templatesLoadingMore.value || !hasMoreTemplates.value) return;
    const text = templatesQueryText.value;
    const nextPage = templatesPage.value + 1;
    templatesLoadingMore.value = true;
    try {
      const result = await TemplatesService.search({
        text,
        page: nextPage,
        pageSize: TEMPLATE_PAGE_SIZE,
      });
      if (templatesQueryText.value !== text) return;
      const known = new Set(templates.value.map(template => template.uid));
      templates.value = [
        ...templates.value,
        ...result.results.filter(template => !known.has(template.uid)),
      ];
      templatesTotalCount.value = result.result_count;
      templatesPage.value = nextPage;
    } finally {
      templatesLoadingMore.value = false;
    }
  }

  async function selectTemplate(uid: string | null): Promise<void> {
    selectedTemplateUid.value = uid;
    selectedTemplate.value = uid
      ? (templates.value.find(template => template.uid === uid) ?? null)
      : null;
    selectedVariantUid.value = null;
    variants.value = [];
    if (!uid) return;

    variantsLoading.value = true;
    try {
      const loaded = await TemplateVariantsService.list(uid);
      if (selectedTemplateUid.value !== uid) return;
      variants.value = loaded;
      // Single variant → no real choice, select it for the user.
      if (loaded.length === 1) {
        selectedVariantUid.value = loaded[0].uid;
      }
    } finally {
      if (selectedTemplateUid.value === uid) {
        variantsLoading.value = false;
      }
    }
  }

  function selectVariant(uid: string | null): void {
    selectedVariantUid.value = uid;
  }

  // ── Stored detail of the selected variant ────────────────────────────
  // Carries the backend-extracted schema + validation errors of the SAVED
  // file (vs. validateLiveDocument, which checks the live Word document).
  // Reloaded whenever the selection or its async validation status changes,
  // so the field list refreshes once a save finishes re-validating.

  const selectedFileDetail = ref<ContractGenerationTemplateVariantInDb | null>(null);
  const selectedFileDetailLoading = ref<boolean>(false);
  let detailRequestToken = 0;

  async function loadSelectedFileDetail(): Promise<void> {
    const templateUid = selectedTemplateUid.value;
    const variant = selectedVariant.value;
    const token = ++detailRequestToken;
    if (!templateUid || !variant) {
      selectedFileDetail.value = null;
      return;
    }
    selectedFileDetailLoading.value = true;
    try {
      const detail = await TemplateVariantsService.get(templateUid, variant.uid);
      if (token === detailRequestToken) selectedFileDetail.value = detail;
    } catch (error) {
      if (token === detailRequestToken) selectedFileDetail.value = null;
      logger.warn('Failed to load template variant detail:', error);
    } finally {
      if (token === detailRequestToken) selectedFileDetailLoading.value = false;
    }
  }

  watch(
    [selectedVariantUid, (): string | null => selectedVariant.value?.template_status ?? null],
    () => {
      void loadSelectedFileDetail();
    }
  );

  /**
   * Re-fetch the variant list of the currently selected template, e.g. when
   * the panel regains focus — variants may have been added/removed from the
   * web app in the meantime. Keeps the selection when it still exists.
   * `silent` skips the loading flag so background polling doesn't flash the
   * variant select's spinner.
   */
  async function refreshVariants({ silent = false }: { silent?: boolean } = {}): Promise<void> {
    const uid = selectedTemplateUid.value;
    if (!uid) return;
    if (!silent) variantsLoading.value = true;
    try {
      const loaded = await TemplateVariantsService.list(uid);
      if (selectedTemplateUid.value !== uid) return;
      variants.value = loaded;
      if (
        selectedVariantUid.value &&
        !loaded.some(variant => variant.uid === selectedVariantUid.value)
      ) {
        selectedVariantUid.value = null;
      }
    } finally {
      if (!silent && selectedTemplateUid.value === uid) {
        variantsLoading.value = false;
      }
    }
  }

  /**
   * Replace the open Word document's content with the selected file's
   * materialized .docx, then validate it once so the user immediately sees
   * the known-good baseline.
   */
  async function openInDocument(): Promise<void> {
    const file = selectedVariant.value;
    if (!file || openingInDocument.value) return;
    openingInDocument.value = true;
    try {
      const blob = await TemplateAuthoringService.downloadMaterialized(file.uid);
      const base64 = await blobToBase64(blob);
      await replaceDocumentFromDocxBase64(base64);
    } finally {
      openingInDocument.value = false;
    }
    await validateLiveDocument();
  }

  // ── Save the live document back to GAIA ─────────────────────────────
  // All three flows export the open Word document (unsaved edits included)
  // and reuse the exact upload + create services the web modals use. On
  // success the result becomes the working context, so its "Validating…"
  // status is immediately visible in the General tab.

  const savingDocument = ref<boolean>(false);

  /**
   * Validate the exported doc just before upload. Returns true to proceed —
   * either there are no blocking (error-severity) issues, or `confirmInvalid`
   * approved uploading despite them. A failed validation *request* (network,
   * etc.) never blocks the upload; we proceed silently.
   */
  async function shouldProceedAfterValidation(
    docx: File,
    confirmInvalid?: ConfirmInvalidUpload
  ): Promise<boolean> {
    if (!confirmInvalid) return true;
    let errors: TemplateValidationError[];
    try {
      const result = await TemplateAuthoringService.validateUpload(docx);
      errors = (result.validation_errors ?? []).filter(e => (e.severity ?? 'error') === 'error');
    } catch {
      return true;
    }
    return errors.length === 0 ? true : confirmInvalid(errors);
  }

  /** All save actions return false when the user cancelled at the validation prompt. */
  async function saveDocumentAsNewTemplate(
    name: string,
    variantName: string,
    description: string | null,
    confirmInvalid?: ConfirmInvalidUpload
  ): Promise<boolean> {
    savingDocument.value = true;
    try {
      const docx = await exportCurrentDocumentAsDocx(`${name}.docx`);
      if (!(await shouldProceedAfterValidation(docx, confirmInvalid))) return false;
      const template = await TemplatesService.create(docx, name, variantName, description);
      templates.value = [template, ...templates.value.filter(t => t.uid !== template.uid)];
      // The fresh template has exactly one variant — selectTemplate auto-picks it.
      await selectTemplate(template.uid);
      return true;
    } finally {
      savingDocument.value = false;
    }
  }

  async function saveDocumentAsNewVariant(
    templateUid: string,
    variantName: string,
    confirmInvalid?: ConfirmInvalidUpload
  ): Promise<boolean> {
    savingDocument.value = true;
    try {
      const templateName =
        templates.value.find(template => template.uid === templateUid)?.name ?? 'template';
      const docx = await exportCurrentDocumentAsDocx(`${templateName}-${variantName}.docx`);
      if (!(await shouldProceedAfterValidation(docx, confirmInvalid))) return false;
      const created = await TemplateVariantsService.create(templateUid, docx, variantName);
      await selectTemplate(templateUid);
      selectedVariantUid.value = created.uid;
      return true;
    } finally {
      savingDocument.value = false;
    }
  }

  async function saveDocumentAsNewVersion(
    templateUid: string,
    file: ContractGenerationTemplateVariantSummary,
    confirmInvalid?: ConfirmInvalidUpload
  ): Promise<boolean> {
    savingDocument.value = true;
    try {
      const docx = await exportCurrentDocumentAsDocx(file.file_name);
      if (!(await shouldProceedAfterValidation(docx, confirmInvalid))) return false;
      await TemplateVariantsService.replaceFile(templateUid, file.uid, docx);
      // Re-read so the freshly-flipped "validating" status surfaces.
      await selectTemplate(templateUid);
      selectedVariantUid.value = file.uid;
      return true;
    } finally {
      savingDocument.value = false;
    }
  }

  async function validateLiveDocument(): Promise<void> {
    if (validationStatus.value === 'running') return;
    validationStatus.value = 'running';
    validationError.value = null;
    try {
      const fileName = selectedVariant.value?.file_name ?? 'document.docx';
      const docx = await exportCurrentDocumentAsDocx(fileName);
      validationResult.value = await TemplateAuthoringService.validateUpload(docx);
      validationStatus.value = 'done';
    } catch (error) {
      validationError.value =
        error instanceof Error ? error.message : 'Failed to validate the document.';
      validationStatus.value = 'error';
    }
  }

  return {
    templates,
    templatesLoading,
    templatesLoadingMore,
    hasMoreTemplates,
    loadMoreTemplates,
    selectedTemplateUid,
    variants,
    variantsLoading,
    selectedVariantUid,
    selectedFileDetail,
    selectedFileDetailLoading,
    openingInDocument,
    fieldCatalog,
    fieldCatalogLoading,
    loadFieldCatalog,
    validationStatus,
    validationResult,
    validationError,
    selectedTemplate,
    selectedVariant,
    searchTemplates,
    selectTemplate,
    selectVariant,
    refreshVariants,
    openInDocument,
    validateLiveDocument,
    savingDocument,
    saveDocumentAsNewTemplate,
    saveDocumentAsNewVariant,
    saveDocumentAsNewVersion,
  };
});
